import { supabase, supabaseService } from './supabase';
import { UserProfile, PortfolioSettings, PortfolioPosition, PerformanceSnapshot } from '../types';

/**
 * Profile service for managing user authentication and profile data
 */

/**
 * Sign up a new user
 * @param email User's email
 * @param password User's password
 * @returns The newly created user object or null if error
 */
export const signUp = async (email: string, password: string) => {
  try {
    const { data, error } = await supabaseService.auth.signUp(email, password);
    
    if (error) throw error;
    
    // Initialize profile and portfolio settings
    if (data.user) {
      await supabaseService.profile.createProfile(data.user.id, email);
    }
    
    return data;
  } catch (error) {
    console.error('Error signing up:', error);
    throw error;
  }
};

/**
 * Sign in an existing user
 * @param email User's email
 * @param password User's password
 * @returns The authenticated user session or null if error
 */
export const signIn = async (email: string, password: string) => {
  try {
    const { data, error } = await supabaseService.auth.signIn(email, password);
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 */
export const signOut = async () => {
  try {
    const { error } = await supabaseService.auth.signOut();
    
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Reset a user's password
 * @param email User's email
 */
export const resetPassword = async (email: string) => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

/**
 * Get the currently authenticated user
 * @returns The current user or null if not authenticated
 */
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error) throw error;
    
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Get the user's profile
 * @returns The user's profile or null if not authenticated
 */
export const getProfile = async (): Promise<UserProfile | null> => {
  try {
    return (await supabaseService.profile.getProfile()) as UserProfile | null;
  } catch (error) {
    console.error('Error getting profile:', error);
    return null;
  }
};

/**
 * Update the user's profile
 * @param profile The profile data to update
 * @returns The updated profile or null if error
 */
export const updateProfile = async (profile: Partial<UserProfile>): Promise<UserProfile | null> => {
  try {
    return (await supabaseService.profile.updateProfile(profile)) as UserProfile | null;
  } catch (error) {
    console.error('Error updating profile:', error);
    return null;
  }
};

/**
 * Get the user's portfolio settings
 * @returns The user's portfolio settings or null if not authenticated
 */
export const getPortfolioSettings = async (): Promise<PortfolioSettings | null> => {
  try {
    return await supabaseService.portfolio.getSettings();
  } catch (error) {
    console.error('Error getting portfolio settings:', error);
    return null;
  }
};

/**
 * Update the user's portfolio settings
 * @param settings The portfolio settings to update
 * @returns The updated settings or null if error
 */
export const updatePortfolioSettings = async (settings: Partial<PortfolioSettings>): Promise<PortfolioSettings | null> => {
  try {
    return await supabaseService.portfolio.updateSettings(settings);
  } catch (error) {
    console.error('Error updating portfolio settings:', error);
    return null;
  }
};

/**
 * Reset portfolio to initial state (for testing/development)
 * @param initialCash Initial cash balance
 * @returns The new portfolio settings or null if error
 */
export const resetPortfolio = async (initialCash: number = 100000): Promise<PortfolioSettings | null> => {
  try {
    const user = await getCurrentUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Delete existing positions
    const { data: positions, error: positionsError } = await supabase
      .from('positions')
      .update({ is_active: false })
      .eq('user_id', user.id);
      
    if (positionsError) throw positionsError;
    
    // Update portfolio settings
    const settings = await supabaseService.portfolio.updateSettings({
      initial_cash: initialCash,
      current_cash: initialCash,
    });
    
    return settings;
  } catch (error) {
    console.error('Error resetting portfolio:', error);
    return null;
  }
};

/**
 * Get the user's active positions
 * @param activeOnly Whether to only return active positions
 * @returns An array of the user's positions
 */
export const getPositions = async (activeOnly: boolean = true): Promise<PortfolioPosition[]> => {
  try {
    return await supabaseService.portfolio.getPositions(activeOnly);
  } catch (error) {
    console.error('Error getting positions:', error);
    return [];
  }
};

/**
 * Buy a stock position
 * @param ticker Stock ticker symbol
 * @param shares Number of shares to buy
 * @param price Price per share
 * @returns The created position or null if error
 */
export const buyPosition = async (
  ticker: string,
  shares: number,
  price: number
): Promise<PortfolioPosition | null> => {
  try {
    // Create position
    const position = await supabaseService.portfolio.createPosition({
      ticker,
      shares,
      entry_price: price,
      entry_date: new Date().toISOString().split('T')[0],
    });
    
    return position;
  } catch (error) {
    console.error('Error buying position:', error);
    return null;
  }
};

/**
 * Sell a stock position
 * @param positionId ID of the position to sell
 * @param exitPrice The price at which to sell
 * @returns The updated position or null if error
 */
export const sellPosition = async (
  positionId: string,
  exitPrice: number
): Promise<PortfolioPosition | null> => {
  try {
    const exitDate = new Date().toISOString().split('T')[0];
    
    // Update position
    const position = await supabaseService.portfolio.closePosition(
      positionId,
      exitPrice,
      exitDate
    );
    
    return position;
  } catch (error) {
    console.error('Error selling position:', error);
    return null;
  }
};

/**
 * Take a snapshot of the portfolio's current value
 * @param currentPrices Current market prices for all positions
 * @returns The created snapshot or null if error
 */
export const takePerformanceSnapshot = async (
  currentPrices: Record<string, number>
): Promise<PerformanceSnapshot | null> => {
  try {
    // Get active positions
    const positions = await getPositions(true);
    
    // Get portfolio settings
    const settings = await getPortfolioSettings();
    
    if (!settings) {
      throw new Error('Portfolio settings not found');
    }
    
    // Calculate total portfolio value
    let portfolioValue = settings.current_cash;
    
    for (const position of positions) {
      const currentPrice = currentPrices[position.ticker] || position.entry_price;
      portfolioValue += position.shares * currentPrice;
    }
    
    // Take snapshot
    return await supabaseService.portfolio.takeSnapshot(portfolioValue);
  } catch (error) {
    console.error('Error taking portfolio snapshot:', error);
    return null;
  }
};

/**
 * Get the user's performance snapshots
 * @param limit Maximum number of snapshots to return
 * @returns Array of performance snapshots
 */
export const getPerformanceSnapshots = async (limit: number = 30): Promise<PerformanceSnapshot[]> => {
  try {
    return await supabaseService.portfolio.getSnapshots(limit);
  } catch (error) {
    console.error('Error getting performance snapshots:', error);
    return [];
  }
};

/**
 * Check if user is authenticated
 * @returns True if authenticated, false otherwise
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const user = await getCurrentUser();
  return !!user;
};

// Export the profile service
export const profileService = {
  signUp,
  signIn,
  signOut,
  resetPassword,
  getCurrentUser,
  getProfile,
  updateProfile,
  getPortfolioSettings,
  updatePortfolioSettings,
  resetPortfolio,
  getPositions,
  buyPosition,
  sellPosition,
  takePerformanceSnapshot,
  getPerformanceSnapshots,
  isAuthenticated,
};

export default profileService; 